import './StateMap.scss';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { GeometryObject, Topology } from 'topojson-specification';
import { ConvergentColorMap6, makeSteppedHeatMap } from '../../utils/colors';
// eslint-disable-next-line import-x/extensions -- bundled static data file, not a JS/TS module
import usData from './states-albers-10m.json';

export interface StateMapProps {
  data: Record<string, number | string>[];
  dataPoint: string;
  height?: number;
  resetValue: string;
  tooltipFormatter: (
    dataPoint: string,
    dataPoints: Record<string, unknown>,
    d: d3.GeoPermissibleObjects
  ) => string;
  width?: number;
  updateSelection: Dispatch<SetStateAction<string>>;
}

export const StateMap = ({
  data,
  dataPoint,
  height = 610,
  resetValue,
  tooltipFormatter,
  width = 960,
  updateSelection
}: StateMapProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const selected = useRef('');

  useEffect(() => {
    if (!ref.current) return undefined;

    const us = usData as unknown as Topology;
    const usObjStates = us.objects.states as GeometryObject;

    const dataPointArray: number[] = data.map(state => Number(state[dataPoint]));
    const dataPointMax = Math.max(...dataPointArray);
    const dataPointMin = Math.min(...dataPointArray);
    const dp: Record<string, number | string> = {};
    data.forEach(d => {
      dp[d.state] = d[dataPoint];
    });
    const svg = d3.select(ref.current);
    svg.selectAll('*').remove();

    const heatMap = makeSteppedHeatMap({
      min: dataPointMin,
      max: dataPointMax,
      stops: ConvergentColorMap6
    });
    // States missing from `data` arrive as NaN and must stay unfilled rather than take the top stop.
    const color = (value: number) => (Number.isNaN(value) ? null : heatMap(value));
    const topoData = topojson.feature(us, usObjStates);
    const path = d3.geoPath().projection(null);

    const zoomed = (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
      const { transform } = event;
      svgPathWrapper.attr('transform', transform.toString());
      svgPathWrapper.attr('stroke-width', String(1 / transform.k));
    };
    const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([1, 8]).on('zoom', zoomed);

    const reset = () => {
      updateSelection(resetValue);
      selected.current = resetValue;
      map.transition().style('fill', null);
      svg
        .transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity,
          d3.zoomTransform(svg.node() as SVGSVGElement).invert([width / 2, height / 2])
        );
    };

    const onClick = (event: MouseEvent, stateLocationData: d3.GeoPermissibleObjects) => {
      const [[x0, y0], [x1, y1]] = path.bounds(stateLocationData);
      const target = event.currentTarget as SVGPathElement;
      const name = (stateLocationData as GeoJSON.Feature).properties?.name;

      event.stopPropagation();

      if (name === selected.current) {
        reset();
        return;
      }
      selected.current = name;
      updateSelection(name);
      map.transition().style('fill', null);
      d3.select(target).transition().style('fill', 'red');
      svg
        .transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(Math.min(8, 0.9 / Math.max((x1 - x0) / width, (y1 - y0) / height)))
            .translate(-(x0 + x1) / 2, -(y0 + y1) / 2),
          d3.pointer(event, svg.node())
        );
    };

    const svgPathWrapper = svg.append('g');
    svg.on('click', reset);
    svg.call(zoom);

    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'state-map-tooltip')
      .style('opacity', 0);
    const tooltipHead = tooltip.append('div').attr('class', 'state-map-tooltip-title');
    const tooltipStat = tooltip.append('div').attr('class', 'state-map-tooltip-data');

    const mouseOver = (event: MouseEvent, d: GeoJSON.Feature) => {
      d3.selectAll('.state-map-path').transition().duration(200);
      d3.selectAll('.state-map-state')
        .transition()
        .duration(200)
        .style('stroke', 'transparent')
        .style('cursor', 'pointer')
        .style('opacity', 0.5);

      d3.select(event.currentTarget as SVGPathElement)
        .transition()
        .duration(200)
        .style('opacity', 1)
        .style('stroke', '#7e7e7e')
        .attr('stroke-width', 2);
      tooltip
        .style('left', `${event.pageX + 15}px`)
        .style('top', `${event.pageY - 28}px`)
        .transition()
        .duration(400)
        .style('opacity', 1);
      tooltipHead.text(d.properties?.name ?? '');
      tooltipStat.text(tooltipFormatter(dataPoint, dp, d));
    };

    const mouseLeave = () => {
      d3.selectAll<SVGPathElement, GeoJSON.Feature>('.state-map-state')
        .transition()
        .duration(200)
        .style('opacity', 1)
        .style('stroke', 'transparent')
        .attr('fill', d => color(Number(dp[d.properties?.name ?? ''])));
      d3.selectAll('.state-map-path')
        .transition()
        .duration(200)
        .style('opacity', 0.5)
        .style('stroke', '#7e7e7e');
      tooltip.transition().duration(300).style('opacity', 0);
    };

    const map = svgPathWrapper
      .append('g')
      .attr('cursor', 'pointer')
      .selectAll('path')
      .data((topoData as GeoJSON.FeatureCollection).features)
      .join('path')
      .attr('d', path)
      .on('click', onClick)
      .attr('fill', (d: GeoJSON.Feature) => color(Number(dp[d.properties?.name ?? ''])))
      .attr('class', 'state-map-state')
      .on('mouseover', mouseOver)
      .on('mouseleave', mouseLeave);

    svgPathWrapper
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', '#7e7e7e')
      .attr('stroke-linejoin', 'round')
      .style('opacity', 0.5)
      .attr('class', 'state-map-path')
      .attr('d', path(topojson.mesh(us, usObjStates, (a, b) => a !== b)));

    return () => {
      tooltip.remove();
    };
  }, [data, dataPoint, height, resetValue, tooltipFormatter, updateSelection, width]);

  return (
    <div className='state-map'>
      <svg ref={ref} style={{ height: `${height}px`, width: `${width}px` }} />
    </div>
  );
};
